import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Clock, CreditCard, Lock, ShieldCheck, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { trackEvent } from "../components/Analytics";
import { HEARD_ABOUT, getVisitSource } from "../lib/visitSource";
import { WhatToBring } from "../components/booking/WhatToBring";

const API = process.env.REACT_APP_BACKEND_URL;
const CONSULTATION_FEE_GBP = 250;
const LOOKUP_KEY = "initial_consultation_gbp";

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      weekday: "long", day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/London",
    });
  } catch { return iso; }
}

export default function CompletePayment() {
  const [params] = useSearchParams();
  const eventUri = params.get("event_uri") || "";
  const inviteeUri = params.get("invitee_uri") || "";
  const consultantSlug = params.get("consultant") || "";

  const [booking, setBooking] = useState(null);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [payLoading, setPayLoading] = useState(false);
  const [heard, setHeard] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [forceEnded, setForceEnded] = useState(false);

  // Countdown to the server's payment deadline (15 min after the slot was picked)
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const deadline = booking?.payment_deadline ? new Date(booking.payment_deadline).getTime() : null;
  const remaining = deadline ? Math.max(0, Math.floor((deadline - now) / 1000)) : null;
  const alreadyPaid = !!booking?.paid;
  const cancelled = !!booking && booking.status !== "active";
  const holdEnded = !!booking && !alreadyPaid && (cancelled || remaining === 0 || forceEnded);
  const mm = remaining === null ? "--" : String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = remaining === null ? "--" : String(remaining % 60).padStart(2, "0");

  useEffect(() => {
    if (!eventUri || !inviteeUri) {
      setLoadState("error");
      setErrorMsg("Missing booking reference. Please pick a time again.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const q = new URLSearchParams({ event_uri: eventUri, invitee_uri: inviteeUri }).toString();
        const resp = await fetch(`${API}/api/booking/details?${q}`);
        if (!resp.ok) {
          const j = await resp.json().catch(() => ({}));
          throw new Error(j.detail || `Could not load booking (${resp.status})`);
        }
        const data = await resp.json();
        if (cancelled) return;
        setBooking(data);
        setLoadState("ready");
        trackEvent("payment_page_view", { consultant: consultantSlug || "any" });
      } catch (e) {
        if (cancelled) return;
        setErrorMsg(e.message || "Could not load booking details.");
        setLoadState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [eventUri, inviteeUri, consultantSlug]);

  const handlePay = async () => {
    if (!booking) return;
    setPayLoading(true);
    trackEvent("start_payment", { consultant: consultantSlug || "any", amount: CONSULTATION_FEE_GBP });
    try {
      const resp = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lookup_key: LOOKUP_KEY,
          origin_url: window.location.origin,
          visit_source: getVisitSource(),
          heard_about: heard,
          consultant_slug: consultantSlug || "",
          reasons: (params.get("reasons") || "").split(",").filter(Boolean),
          event_uri: eventUri,
          invitee_uri: inviteeUri,
          invitee_email: booking.invitee_email || "",
          invitee_name: booking.invitee_name || "",
        }),
      });
      if (resp.status === 410 || resp.status === 409) {
        const err = await resp.json().catch(() => ({}));
        if (resp.status === 410) setForceEnded(true);
        else setBooking((b) => ({ ...b, paid: true }));
        toast.error(err.detail);
        setPayLoading(false);
        return;
      }
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Payment could not be started (${resp.status})`);
      }
      const data = await resp.json();
      if (!data.checkout_url) throw new Error("Missing checkout URL");
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(e.message || "Payment failed to start. Please try again.");
      setPayLoading(false);
    }
  };

  const consultant = booking && booking.consultant;
  const whenStr = useMemo(() => booking ? formatWhen(booking.start_time) : "", [booking]);

  return (
    <div data-testid="complete-payment-page">
      <SEO title="Confirm your booking with payment" path="/complete-payment" />

      <section className="bg-cream-100 border-b border-bronze-light/30">
        <div className="container-ccc py-14 max-w-5xl">
          {!alreadyPaid && <div data-testid="hold-countdown" className={`inline-flex items-center gap-2.5 rounded-full border px-4 py-2 ${holdEnded ? "border-red-200 bg-red-50 text-red-700" : "border-bronze-light bg-white text-bronze-dark"}`}>
            <Clock size={18} weight="duotone" />
            {holdEnded ? (
              <span className="text-sm font-medium">Hold ended - slot released</span>
            ) : (
              <span className="text-sm">Slot held for <b data-testid="hold-countdown-time" className="font-mono text-base tabular-nums">{mm}:{ss}</b></span>
            )}
          </div>}
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
            Confirm your booking <em className="font-serif text-bronze-dark">with payment.</em>
          </h1>
          {alreadyPaid ? (
            <p className="mt-6 text-ink-muted text-lg max-w-3xl leading-relaxed">Your payment has been received and this appointment is confirmed.</p>
          ) : holdEnded ? (
            <p className="mt-6 text-ink-muted text-lg max-w-3xl leading-relaxed">This slot is no longer held. You can choose a new time below.</p>
          ) : (
            <p className="mt-6 text-ink-muted text-lg max-w-3xl leading-relaxed">
              Your slot is held for you, but <b className="text-ink">it is not booked until you pay.</b>
            </p>
          )}
          {!alreadyPaid && !holdEnded && <div data-testid="no-payment-no-booking-notice" className="mt-6 max-w-3xl border-l-2 border-bronze-dark pl-5 py-1 text-sm text-ink leading-relaxed">
            <b>No payment, no booking.</b> If payment isn&apos;t completed within 15 minutes of choosing your time, your appointment is cancelled automatically, the slot is released for other patients, and you won&apos;t be charged. We&apos;ll email you if this happens.
          </div>}
        </div>
      </section>

      <section className="container-ccc py-12 max-w-5xl">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            {loadState === "loading" && (
              <div data-testid="booking-loading" className="bg-white border border-bronze-light/40 rounded-md p-8 flex items-center gap-3 text-ink-muted">
                <span className="inline-block w-4 h-4 border-2 border-bronze-dark border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Loading your booking details…</span>
              </div>
            )}

            {loadState === "error" && (
              <div data-testid="booking-error" className="bg-white border border-red-200 rounded-md p-8">
                <div className="flex items-center gap-2 text-red-700">
                  <WarningCircle size={20} weight="fill" />
                  <b>We couldn&apos;t load your booking</b>
                </div>
                <p className="mt-4 text-sm text-ink-muted">{errorMsg}</p>
                <div className="mt-6">
                  <Link to="/enquiry" data-testid="booking-error-retry" className="btn-primary">Choose a time again</Link>
                </div>
              </div>
            )}

            {loadState === "ready" && booking && holdEnded && (
              <div data-testid="booking-hold-ended" className="bg-white border border-red-200 rounded-md p-8">
                <div className="flex items-center gap-2 text-red-700">
                  <WarningCircle size={20} weight="fill" />
                  <b>This booking was not confirmed</b>
                </div>
                <p className="mt-4 text-sm text-ink-muted">
                  {cancelled
                    ? `This booking${whenStr ? ` for ${whenStr}` : ""} has been cancelled, so it can't be paid for. No charge has been made.`
                    : `Payment wasn't completed within 15 minutes, so the slot${whenStr ? ` on ${whenStr}` : ""} has been released. No charge has been made.`}
                </p>
                <div className="mt-6">
                  <Link to="/enquiry" data-testid="booking-hold-ended-rebook" className="btn-primary">Choose a time again</Link>
                </div>
              </div>
            )}

            {loadState === "ready" && alreadyPaid && (
              <div data-testid="booking-already-paid" className="bg-white border border-bronze-light/40 rounded-md p-8">
                <div className="flex items-center gap-2 text-bronze-dark">
                  <CheckCircle size={20} weight="fill" />
                  <b>Already paid - you&apos;re all set</b>
                </div>
                <p className="mt-4 text-sm text-ink-muted">
                  Your appointment{whenStr ? ` on ${whenStr}` : ""} is confirmed. Your confirmation email has the calendar invite and prep sheet.
                </p>
                <div className="mt-6"><Link to="/" className="btn-secondary">Back to home</Link></div>
              </div>
            )}

            {loadState === "ready" && booking && !holdEnded && !alreadyPaid && (
              <div className="bg-white border border-bronze-light/40 rounded-md p-8" data-testid="booking-summary">
                <div className="eyebrow">Your booking</div>
                <h2 className="font-serif text-2xl mt-2">{booking.event_name || "Consultation"}</h2>
                <dl className="mt-6 space-y-4 text-sm">
                  <div className="flex gap-3">
                    <dt className="w-24 shrink-0 text-ink-muted">When</dt>
                    <dd className="text-ink font-medium" data-testid="booking-when">{whenStr}</dd>
                  </div>
                  {consultant && (
                    <div className="flex gap-3">
                      <dt className="w-24 shrink-0 text-ink-muted">Consultant</dt>
                      <dd className="text-ink" data-testid="booking-consultant">{consultant}</dd>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <dt className="w-24 shrink-0 text-ink-muted">Where</dt>
                    <dd className="text-ink">{booking.location_label || "ACCI Building, Cambridge University Hospitals, Hills Road, Cambridge CB2 0QQ"}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-24 shrink-0 text-ink-muted">Name</dt>
                    <dd className="text-ink">{booking.invitee_name}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-24 shrink-0 text-ink-muted">Email</dt>
                    <dd className="text-ink">{booking.invitee_email}</dd>
                  </div>
                </dl>

                <hr className="my-8 border-bronze-light/30" />

                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-ink-muted">Consultation fee</span>
                  <span className="font-serif text-4xl text-ink">£{CONSULTATION_FEE_GBP}</span>
                </div>
                <p data-testid="payment-fee-note" className="mt-2 text-xs text-ink-muted">1-hour new consultation only. Any tests or scans are charged separately.</p>

                <label className="mt-6 block">
                  <span className="text-xs uppercase tracking-[0.16em] text-ink-muted">How did you hear about us?</span>
                  <select value={heard} onChange={(e) => setHeard(e.target.value)} data-testid="payment-heard-select"
                    className="mt-1 w-full rounded-sm border border-stone-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-bronze-dark">
                    <option value="" disabled>Please choose…</option>
                    {HEARD_ABOUT.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </label>

                <button
                  type="button"
                  onClick={handlePay}
                  disabled={payLoading || !heard}
                  data-testid="confirm-and-pay-btn"
                  className="mt-6 w-full btn-primary text-base py-4 inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Lock size={18} weight="bold" />
                  {payLoading ? "Redirecting to secure checkout…" : `Pay £${CONSULTATION_FEE_GBP} and confirm booking`}
                </button>
                <p className="mt-3 text-[11px] text-ink-muted text-center">
                  Powered by Stripe · your card details never touch our servers.
                </p>

                <div className="mt-6 text-xs text-ink-muted">
                  Changed your mind? Just close this tab - without payment the slot is released automatically and nothing is booked.
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-5 space-y-5">
            <WhatToBring testId="payment-what-to-bring" />
            <div className="p-6 bg-cream-100 border border-bronze-light/40 rounded-md">
              <div className="eyebrow flex items-center gap-2 text-bronze-dark">
                <CheckCircle size={16} weight="duotone" /> After payment
              </div>
              <ul className="mt-4 text-sm text-ink-muted space-y-2 list-disc list-inside">
                <li>On-screen confirmation once payment goes through.</li>
                <li>Email confirmation with a calendar invite (.ics) - drops straight into Google, Apple or Outlook.</li>
                <li>Short prep PDF so you know exactly what to bring.</li>
              </ul>
            </div>
            <div className="p-6 bg-white border border-stone-200/60 rounded-md">
              <div className="eyebrow flex items-center gap-2 text-bronze-dark">
                <ShieldCheck size={16} weight="duotone" /> Refund policy
              </div>
              <ul className="mt-4 text-sm text-ink-muted space-y-2">
                <li>Full refund if you cancel 48+ hours ahead.</li>
                <li>50% refund between 24-48 hours before.</li>
                <li>No refund inside 24 hours.</li>
              </ul>
            </div>
            <div className="p-6 bg-white border border-stone-200/60 rounded-md">
              <div className="eyebrow flex items-center gap-2 text-bronze-dark">
                <CreditCard size={16} weight="duotone" /> Cards accepted
              </div>
              <p className="mt-3 text-sm text-ink-muted">
                Visa, Mastercard, American Express - processed securely by Stripe with 3-D Secure where required.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
